// ============================================================
// useAdminBadgeCounts.js
// The two counts that make the portal worth opening on a phone:
// new trial requests, and unhandled contact enquiries.
//
// Lifted out of SideNav so the mobile top bar can show the same
// numbers WITHOUT the drawer being open. On mobile the rail is
// off-canvas, so if these counts only lived inside it there would be
// no way to see that anything needs attention without opening a
// drawer first — which is the whole reason the portal gets opened on
// a phone at all.
//
// Refetched on pathname change so the numbers stay current after a
// triage action, matching the behaviour this had inside SideNav.
//
// Fail-soft by contract: a rejected fetch leaves the count at 0 and
// the badge hidden. A count is a convenience; it must never break
// navigation.
//
// `enabled` exists so SideNav can skip its own fetch when AppShell
// has already passed counts down — without it the drawer opening
// would fire a second, duplicate pair of requests. The hook is still
// called unconditionally (Rules of Hooks); only the effect body is
// skipped.
// ============================================================

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listAdminTrialRequests } from '../api/adminTrialRequests';
import { listAdminContactMessages } from '../api/adminContactMessages';

export default function useAdminBadgeCounts({ enabled = true } = {}) {
  const location = useLocation();
  const [trialNew, setTrialNew] = useState(0);
  const [contactNew, setContactNew] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    listAdminTrialRequests({ pageSize: 1 })
      .then((res) => {
        if (!cancelled) setTrialNew((res.statusCounts && res.statusCounts.New) || 0);
      })
      .catch(() => { /* badge stays hidden */ });
    return () => { cancelled = true; };
  }, [enabled, location.pathname]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    listAdminContactMessages({ pageSize: 1 })
      .then((res) => {
        if (!cancelled) setContactNew((res.statusCounts && res.statusCounts.new) || 0);
      })
      .catch(() => { /* badge stays hidden */ });
    return () => { cancelled = true; };
  }, [enabled, location.pathname]);

  return { trialNew, contactNew };
}
